/**
 * Detonation セクション: DETONATE ボタン + タイムラインスクラバ + Reset。
 *
 * 設計書 FR-13 / FR-15 / FR-16 / 10.2。プロトタイプのセクション構造と
 * `.btn.detonate` スタイル（accent 背景 + 0.25em tracking）に合わせる。
 *
 * 責務:
 * - DETONATE クリック → `onDetonate` callback を発火（rAF 再生は main.ts）
 * - Reset クリック → `progress = 0`, `isAnimating = false`
 * - スクラバは `progress` と双方向バインド（`isAnimating` 中は rAF 優先）
 * - `isAnimating = true` の間 DETONATE を disabled
 *
 * `onDetonate` を callback にしているのは、UI 層が rAF ループや時間管理を
 * 直接持たないようにするため。main.ts が `render` と時間軸をつないだ上で
 * `onDetonate(() => start())` を登録する。
 */

import type { AppState, Store } from '../../state';

/** `mountTimeline` の戻り値。 */
export interface TimelineHandle {
  /** teardown: DOM + subscribe + listener 解除。 */
  destroy: () => void;
  /**
   * DETONATE クリック時に呼ぶ callback を登録する。1 回の呼び出しで
   * 上書き。`null` を渡すと解除。（main.ts が rAF を発火する窓口。）
   */
  onDetonate: (cb: () => void) => void;
}

/** タイムラインスクラバの離散化分解能。3000ms / 3001 段 ≒ 1ms 精度。 */
const TIMELINE_STEPS = 1000;

/**
 * Detonation セクションをマウントする。
 *
 * @param root - Controls 内の新規 `<div>` を想定
 * @param store - グローバルストア
 * @returns `TimelineHandle`
 */
export function mountTimeline(root: HTMLElement, store: Store<AppState>): TimelineHandle {
  const section = document.createElement('div');

  const label = document.createElement('div');
  label.className = 'section-label';
  label.textContent = 'Detonation';
  section.appendChild(label);

  // ── DETONATE ボタン ──────────────────────────────────────────
  const detonate = document.createElement('button');
  detonate.type = 'button';
  detonate.className = 'btn detonate';
  // "▸" は Unicode 幾何記号。絵文字ではない。
  detonate.textContent = '▸ DETONATE';
  section.appendChild(detonate);

  // ── タイムラインスクラバ ─────────────────────────────────────
  const wrap = document.createElement('div');
  wrap.className = 'timeline-wrap';

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '0';
  slider.max = String(TIMELINE_STEPS);
  slider.step = '1';
  slider.value = String(Math.round(store.get().progress * TIMELINE_STEPS));
  slider.className = 'timeline';
  wrap.appendChild(slider);

  // Q7 確定: 旧マーク（00:00 / CRACK / BURST / 03:00）は削除。v1.1 の
  // 二相モデル（spec §3.3）では明示的な位相境界を UI に出さない。
  section.appendChild(wrap);

  // ── Reset ボタン ─────────────────────────────────────────────
  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'btn ghost btn-reset';
  reset.textContent = 'Reset';
  section.appendChild(reset);

  // ── ハンドラ / 購読 ──────────────────────────────────────────
  let detonateCb: (() => void) | null = null;

  const onDetonateClick = (): void => {
    if (store.get().isAnimating) return;
    detonateCb?.();
  };
  detonate.addEventListener('click', onDetonateClick);

  const onResetClick = (): void => {
    store.set({ progress: 0, isAnimating: false });
  };
  reset.addEventListener('click', onResetClick);

  // スクラバ操作中は `isAnimating` を尊重せず手動 scrub を許可。
  // （プロトタイプでは再生中の scrub を block していたが、仕様書は
  //  手動 scrub 許可を明記しているため仕様優先。）
  const onSliderInput = (): void => {
    const n = Number(slider.value);
    if (!Number.isFinite(n)) return;
    const progress = n / TIMELINE_STEPS;
    // ユーザが scrub した瞬間に rAF 再生は中断したい
    store.set({ progress, isAnimating: false });
  };
  slider.addEventListener('input', onSliderInput);

  // Phase H Step 2: 自動アニメ中の「クリック + ホールド（value 非変化）」でも
  // 即座に auto-anim を止められるよう pointerdown で isAnimating を落とす。
  // input ハンドラは value 変化時のみ発火するため、thumb を掴んだだけの
  // 操作（値は変わらないがユーザーは「停めた」と感じる）を補償する。
  const onSliderPointerDown = (): void => {
    if (store.get().isAnimating) store.set({ isAnimating: false });
  };
  slider.addEventListener('pointerdown', onSliderPointerDown);

  const unsubscribe = store.subscribe((state, prev) => {
    if (state.progress !== prev.progress) {
      const rounded = String(Math.round(state.progress * TIMELINE_STEPS));
      if (slider.value !== rounded) slider.value = rounded;
    }
    if (state.isAnimating !== prev.isAnimating) {
      detonate.disabled = state.isAnimating;
    }
  });

  // 初期 disabled 反映
  detonate.disabled = store.get().isAnimating;

  root.appendChild(section);

  return {
    destroy: (): void => {
      unsubscribe();
      detonate.removeEventListener('click', onDetonateClick);
      reset.removeEventListener('click', onResetClick);
      slider.removeEventListener('input', onSliderInput);
      slider.removeEventListener('pointerdown', onSliderPointerDown);
      section.remove();
    },
    onDetonate: (cb: () => void): void => {
      detonateCb = cb;
    },
  };
}
